import _ from 'lodash';
import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter } from '@patsnap/tahiti-core';
import { EmbodimentClaimFeaturesModel } from '../../models/embodiment-claim-features-model';
import { GetSpecificationClaimFeaturesApi, type ApiGetSpecificationClaimFeaturesResponse } from '@/infrastructure/api-functions/core-drafting-api/get-specification-claim-features-api';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import type { EmbodimentClaimFeaturesState, ClaimFeatureItem } from '../../types';
import type { EmbodimentClaimFeaturesService } from '../embodiment-claim-features-service';

@injectable()
export class EmbodimentClaimFeaturesServiceImpl implements EmbodimentClaimFeaturesService {
    private _onModelDataChangeEmitter = new Emitter<EmbodimentClaimFeaturesState>();
    public readonly onStateChange = this._onModelDataChangeEmitter.event;

    constructor(
        @inject(EmbodimentClaimFeaturesModel)
        private _model: EmbodimentClaimFeaturesModel,
        @inject(GetSpecificationClaimFeaturesApi)
        private _fetch: GetSpecificationClaimFeaturesApi,
        @inject(CurrentTaskService)
        private _currentTaskService: CurrentTaskService,
    ) {
        this._model.onStatesChange(this._emitModelDataChange.bind(this));
    }

    private _emitModelDataChange(): void {
        this._onModelDataChangeEmitter.fire(this.getModelData());
    }

    public getModelData(): EmbodimentClaimFeaturesState {
        return this._model.modelData;
    }

    private _updateModelData(value: Partial<EmbodimentClaimFeaturesState>): void {
        this._model.update(state => ({
            ...state,
            ...value,
        }));
    }

    private _formatClaimFeatures(featureTree: ApiGetSpecificationClaimFeaturesResponse['content']['feature_tree']): ClaimFeatureItem[] {
        const claimList: ClaimFeatureItem[] = [];

        featureTree.forEach(item => {
            const features: string[] = [];

            item.claim_features?.forEach(feature => {
                features.push(...(feature.concepts || []));
            });

            claimList.push({
                num: item.num,
                features: _.uniq(features),
            });
        });

        return claimList;
    }

    public async initClaimFeatures(): Promise<void> {
        this._updateModelData({ isLoading: true });

        let _tempResult: ApiGetSpecificationClaimFeaturesResponse['content']['feature_tree'] = [];

        const stream = this._fetch.getSpecificationClaimFeatures({
            task_id: this._currentTaskService.taskId,
        })
            .onMessage(res => {
                _tempResult = res.data.content.feature_tree || [];
            })
            .onFinish(() => {
                const formattedFeatures = this._formatClaimFeatures(_tempResult);
                this._updateModelData({
                    claimFeatures: formattedFeatures,
                    isLoading: false,
                });
            })
            .onError(() => {
                this._updateModelData({
                    claimFeatures: [],
                    isLoading: false,
                });
            });

        await stream.start();
    }
}
